import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:8000/api";

/**
 * Stock Medicine Search API Service
 * Provides real-time search for individual medicine items in prescription forms
 */
class StockMedicineSearchService {
  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Add request interceptor to include auth token
    this.api.interceptors.request.use(
      (config) => {
        const user = localStorage.getItem("user");
        if (user) {
          const userData = JSON.parse(user);
          if (userData.token) {
            config.headers.Authorization = `Token ${userData.token}`;
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  /**
   * Search for stock medicines
   * @param {Object} searchParams - Search parameters
   * @param {string} searchParams.search - Search query
   * @param {number} searchParams.page - Page number
   * @param {number} searchParams.page_size - Items per page
   * @param {string} searchParams.category_id - Category filter
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} Search results
   */
  async searchStockMedicines(searchParams = {}) {
    try {
      const params = new URLSearchParams();

      // Trim search query to handle whitespace issues
      if (searchParams.search) {
        params.append("search", searchParams.search.trim());
      }
      if (searchParams.page) {
        params.append("page", searchParams.page);
      }
      if (searchParams.page_size) {
        params.append("page_size", searchParams.page_size);
      }
      if (searchParams.category_id) {
        params.append("category_id", searchParams.category_id);
      }

      const response = await this.api.get(`/stock-medicines/search/?${params}`);
      return response.data;
    } catch (error) {
      console.error("Stock medicine search error:", error);
      throw error;
    }
  }

  /**
   * Quick search for autocomplete suggestions
   * @param {string} query - Search query
   * @param {number} limit - Maximum number of results
   * @param {string} token - Authentication token
   * @returns {Promise<Array>} Array of medicine suggestions
   */
  async quickSearch(query, limit = 10) {
    try {
      // Trim query to handle whitespace issues
      const trimmedQuery = query ? query.trim() : '';
      console.log("🔍 Service: Quick search for:", trimmedQuery);
      const results = await this.searchStockMedicines({
        search: trimmedQuery,
        page: 1,
        page_size: limit,
      });
      console.log("🔍 Service: API response for", query, ":", results);
      return results.results || [];
    } catch (error) {
      console.error("Quick search error:", error);
      return [];
    }
  }
}

// Create and export a singleton instance
const stockMedicineSearchService = new StockMedicineSearchService();
export default stockMedicineSearchService;
